const KGS_IN_LBS = 0.453592
const LBS_IN_KG = 2.20462

const kgToLbs = (kg = 0) => Math.round(kg / KGS_IN_LBS)
const lbsToKg = (lbs = 0) => Math.round(lbs / LBS_IN_KG)

const isApiDietTargetSystem = (offer) => {
  return offer.brandOfferId
}

const isNpsTargetSystem = (offer) => {
  return offer.priceProductObjectId
}

const getOfferId = (offer) => {
  // Offer ID data field depends from BE target system: api-diet or nnp2
  return isApiDietTargetSystem(offer) ? offer.brandOfferId : offer.priceProductObjectId
}

const getBasicOffers = (offers) => {
  return offers
    .filter((offer) => offer.type && offer.type.toLowerCase() === "basic")
    .sort((a, b) => a.customData.sequence - b.customData.sequence)
}

const isBasicPlanOffer = (offer) => {
  return offer.type && offer.type.toLowerCase() === "basic"
}

const isOfferTrial = (offer) => {
  return !!offer.customData.trial_price
}

const getOfferWeeks = (offer) => {
  const weeks = offer?.customData?.weeks

  if (!weeks) return 0

  return parseInt(weeks)
}

const getOfferMonths = (offer) => {
  const months = offer?.customData?.months

  // Fallback to default 1 month, as
  // this is the usual product case.
  // For example, the 1 week trial offer
  // has monthly charges after trial end.
  // This is helpful to handle empty 'months'
  // fields left in admin by products.
  if (!months) return 1

  return parseInt(months)
}

const hasOfferChance = (offer) => {
  return !!offer.customData.chance_price
}

const getVariantPagePath = ({ pagePath, pipelineConfig }) => {
  const paths = pipelineConfig.variants
    ? pipelineConfig.variants[pipelineConfig.variant].pagePaths
    : pipelineConfig.pagePaths

  const trimmedPath = pagePath.replace(/^\/+|\/+$/g, "")

  return paths[trimmedPath]
}

const getVariantPageUrl = ({ pagePath, pipelineConfig }) => {
  let variantPagePath = getVariantPagePath({ pagePath, pipelineConfig })
  const urlPrefix = `${window.location.origin}${pipelineConfig.pathPrefix}`

  if (variantPagePath.startsWith("/")) variantPagePath = variantPagePath.slice(1)
  if (!variantPagePath.endsWith("/")) variantPagePath += "/"

  return urlPrefix + variantPagePath
}

const getLocaleFromConfig = () => {
  const locale = pipelineConfig.language || "en"

  const localesMap = {
    sr: "sr-Latn",
  }

  return localesMap[locale] || locale
}

const getDaysTillGoalDate = (values, isMetric) => {
  const { weightKg, weightLbs, targetWeightKg, targetWeightLbs, goalPaceKg, goalPaceLbs } = values

  // Goal data calculation provided by the product team:
  // ((weightKg - targetWeightKg - 5kg or 1/3 of current weight for the 1st week (whichever is smaller)) / xkg/week  + 1) * 7 = days to reach goal
  // Example:
  // ((90 - 78 - 5 [because 90/3 > 5kg]) / 2 + 1) * 7 = 35 days

  let daysTillGoalDate

  if (isMetric) {
    const goalPace = goalPaceKg || getDefaultGoalPaceKg()
    const weightDiff = Math.abs(weightKg - targetWeightKg)
    const initialLoss = Math.min(5, weightDiff / 3)
    const remainingWeight = weightDiff - initialLoss
    daysTillGoalDate = (remainingWeight / goalPace + 1) * 7
  } else {
    const goalPace = goalPaceLbs || getDefaultGoalPaceLbs()
    const weightDiff = Math.abs(weightLbs - targetWeightLbs)
    const initialLoss = Math.min(11, weightDiff / 3)
    const remainingWeight = weightDiff - initialLoss
    daysTillGoalDate = (remainingWeight / goalPace + 1) * 7
  }

  return Math.abs(daysTillGoalDate)
}

const getDaysTillDate = (eventDate) => {
  if (!eventDate) return 0

  const today = new Date()
  const date = new Date(eventDate)

  const timeDiff = date.getTime() - today.getTime()

  return Math.round(timeDiff / (1000 * 3600 * 24))
}

const getFasterProgressDaysDiff = (values, isMetric) => {
  const { importantEventDate } = values

  const daysTillEventDate = getDaysTillDate(importantEventDate)
  const daysTillGoalDate = getDaysTillGoalDate(values, isMetric)

  // Product requirement:
  // Enable faster progress date only if
  // certain days thresholds presented
  const threshold = daysTillEventDate ? 10 : 35
  const isEnabled = daysTillEventDate
    ? daysTillGoalDate - daysTillEventDate >= threshold
    : daysTillGoalDate >= threshold

  return isEnabled ? 5 : 0
}

const getEventDateWeight = (values, isMetric) => {
  const { importantEventDate, weightKg, weightLbs, goalPaceKg, goalPaceLbs } = values

  // Goal data calculation provided by the product team:
  // eventDateWeight = (weightKg - daysTillEventDate) / goalPaceKg * 7

  let eventDateWeight
  const daysTillEventDate = getDaysTillDate(importantEventDate)

  if (isMetric) {
    eventDateWeight = weightKg - (daysTillEventDate / 7) * goalPaceKg
  } else {
    eventDateWeight = weightLbs - (daysTillEventDate / 7) * goalPaceLbs
  }

  return Math.round(eventDateWeight)
}

const getLocalizedDateLabel = (date, isIncludeYear = true, isLongMonth) => {
  const options = { day: "numeric", month: "short" }

  if (isIncludeYear) {
    options.year = "numeric"
  }

  if (isLongMonth) {
    options.month = "long"
  }

  const locale = getLocaleFromConfig()
  const localizedDateLabel = date.toLocaleDateString(locale, options)

  return localizedDateLabel
}

const getCompactLocalizedDateLabel = (date) => {
  const options = { month: "short", year: "numeric" }
  const locale = getLocaleFromConfig()
  const compactLocalizedDateLabel = date.toLocaleDateString(locale, options).replace(/,/g, "")

  return compactLocalizedDateLabel
}

const getInputDateFormatted = (date) => {
  const year = date.getFullYear()
  const month = String(date.getMonth() + 1).padStart(2, "0") // Months are 0-indexed
  const day = String(date.getDate()).padStart(2, "0")

  return `${year}-${month}-${day}`
}

// On the BE we calculate the next payment date based on Riga timezone
const getNextPaymentDateAfterPurchase = (offer) => {
  // Get current date in Riga timezone
  const rigaTime = new Date().toLocaleString("en-US", { timeZone: "Europe/Riga" })
  const rigaDate = new Date(rigaTime)

  const weeks = getOfferWeeks(offer)
  const months = getOfferMonths(offer)

  const days = weeks ? weeks * 7 : months * 30

  // Add days
  rigaDate.setDate(rigaDate.getDate() + days)

  // Return in local timezone
  return rigaDate
}

//
// BMI
//

const getNormalizedHeight = (values, isMetric) => {
  const { heightCm, heightFt, heightIn } = values

  if (isMetric) {
    return heightCm
  } else {
    return Math.round(heightFt * 30.48 + heightIn * 2.54)
  }
}

const getNormalizedWeight = (values, isMetric) => {
  const { weightKg, weightLbs } = values

  if (isMetric) {
    return weightKg
  } else {
    return Math.round(weightLbs * KGS_IN_LBS)
  }
}

const getBmi = (values, isMetric) => {
  const weight = getNormalizedWeight(values, isMetric)
  const height = getNormalizedHeight(values, isMetric)
  const bmi = weight / (height / 100.0) ** 2

  return parseFloat(bmi.toFixed(1))
}

const getBmiPercents = (bmi) => {
  const percents = ((bmi - 12.01) * 100) / (29.99 - 12.01)

  if (percents > 98) {
    return 98
  } else if (percents < 0) {
    return 0
  }

  return parseFloat(percents.toFixed())
}

const getFirstWeekLoss = ({ values, isMetric }) => {
  const { weightKg, weightLbs, targetWeightKg, targetWeightLbs } = values

  const currentWeight = isMetric ? weightKg : weightLbs
  const targetWeight = isMetric ? targetWeightKg : targetWeightLbs

  // Product requirement:
  // First week is always 5kg or 1/3 of the current weight minus target weight, whichever is smaller
  const targetDiff = currentWeight - targetWeight
  const maxFirstWeekLoss = isMetric ? 5 : kgToLbs(5)

  // Fallback for the weight gain scenario
  if (targetDiff <= 0) {
    return Math.ceil(maxFirstWeekLoss)
  }

  const firstWeekLoss = Math.min(targetDiff / 3, maxFirstWeekLoss)

  return Math.ceil(firstWeekLoss)
}

const isWeightGain = (values, isMetric) => {
  const { weightKg, weightLbs, targetWeightKg, targetWeightLbs } = values

  const weightGain = isMetric ? weightKg - targetWeightKg : weightLbs - targetWeightLbs

  return weightGain <= 0
}

const getProjectedWeightLoss = ({ values, isMetric, months }) => {
  months = Number(months) || 0
  const weeks = Math.max(0, months * 4 - 1)
  const { weightKg, weightLbs, targetWeightKg, targetWeightLbs, goalPaceKg, goalPaceLbs } = values

  const excessWeight = isMetric ? weightKg - targetWeightKg : weightLbs - targetWeightLbs

  if (excessWeight <= 0) {
    return isMetric ? targetWeightKg : targetWeightLbs
  }

  const initialLoss = isMetric ? Math.min(5, excessWeight / 3) : Math.min(11, excessWeight / 3)

  const remainingWeight = excessWeight - initialLoss
  const pace = isMetric ? goalPaceKg : goalPaceLbs
  const subsequentLoss = Math.min(remainingWeight, pace * weeks)

  const projectedWeightLoss = initialLoss + subsequentLoss

  return Math.round(Math.min(projectedWeightLoss, excessWeight))
}

const getGolaPaceKgValues = () => [0.5, 1, 2]
const getDefaultGoalPaceKg = () => 1
const getGolaPaceLbsValues = () => [1, 2.5, 4.5]
const getDefaultGoalPaceLbs = () => 2.5

const debounce = (fn, delay) => {
  let timeoutId

  return function (...args) {
    clearTimeout(timeoutId)

    timeoutId = setTimeout(() => {
      fn.apply(this, args)
    }, delay)
  }
}

const isUserLocale = (locale, pipelineConfig) => {
  return pipelineConfig.pathPrefix.toLowerCase().includes(locale)
}

const convertArabicDigits = (val) => {
  return val.replace(/[\u0660-\u0669]/g, (d) => d.charCodeAt(0) - 0x0660)
}

export {
  KGS_IN_LBS,
  LBS_IN_KG,
  kgToLbs,
  lbsToKg,
  getBasicOffers,
  isBasicPlanOffer,
  getOfferId,
  isOfferTrial,
  getOfferWeeks,
  getOfferMonths,
  hasOfferChance,
  getVariantPagePath,
  getVariantPageUrl,
  getLocalizedDateLabel,
  getCompactLocalizedDateLabel,
  getNextPaymentDateAfterPurchase,
  getDaysTillDate,
  getDaysTillGoalDate,
  getInputDateFormatted,
  getEventDateWeight,
  getNormalizedHeight,
  getNormalizedWeight,
  getBmi,
  getBmiPercents,
  getGolaPaceKgValues,
  getDefaultGoalPaceKg,
  getGolaPaceLbsValues,
  getDefaultGoalPaceLbs,
  getFasterProgressDaysDiff,
  isApiDietTargetSystem,
  isNpsTargetSystem,
  getFirstWeekLoss,
  isWeightGain,
  getProjectedWeightLoss,
  debounce,
  isUserLocale,
  getLocaleFromConfig,
  convertArabicDigits,
}
